import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import enGB from 'date-fns/locale/en-GB'
import fr from 'date-fns/locale/fr'
import es from 'date-fns/locale/es'
import de from 'date-fns/locale/de'
import ptBR from 'date-fns/locale/pt-BR'
import it from 'date-fns/locale/it'
import DatePicker, { registerLocale } from 'react-datepicker'
import { Field } from 'redux-form'
import { isEmpty, pathOr } from 'ramda'
import { generate as generateId } from 'shortid'

import _debounce from 'lodash.debounce'
import parse from 'html-react-parser'
import CustomScrollBar from '../../../../shared/CustomScrollBar'
import { FILTER_DATES } from '../../../constants'
import { URLDateParam } from '../../../utils'
import offersMockedData from '../../../helpers/drupalSettings'
import { isDesktop, isTouchDevice } from '../../../../../helpers/browser'

import 'react-datepicker/dist/react-datepicker.css'
import './dates.scss'
import { PAGE_HOTEL } from '../../../../../constants'

const oneDayBooking = pathOr(null, ['drupalSettings', 'oneDayBooking'], window)
let location = ''

class Dates extends PureComponent {
  constructor(props) {
    super(props)
    this.datesRange = []
    this.state = {
      width: window.innerWidth,
      isForceFilterUpdate: false,
    }

    this.start = null
    this.end = null
    const { dates, locale } = this.props

    if (dates && dates.length === 2) {
      this.start = URLDateParam(dates[0])
      this.end = URLDateParam(dates[1])
    }

    this.monthDays = []

    this.datepickerRef = React.createRef()
    this.inputStart = React.createRef()
    this.inputEnd = React.createRef()

    switch (locale) {
      case 'en':
        registerLocale('en-gb', enGB)
        location = 'en-gb'
        break
      case 'fr':
        registerLocale('fr', fr)
        location = 'fr'
        break
      case 'es':
        registerLocale('es', es)
        location = 'es'
        break
      case 'de':
        registerLocale('de', de)
        location = 'de'
        break
      case 'pt-br':
        registerLocale('pt-BR', ptBR)
        location = 'pt-BR'
        break
      case 'it':
        registerLocale('it', it)
        location = 'it'
        break
      default:
        registerLocale('en-gb', enGB)
        location = 'en-gb'
    }
  }

  componentDidMount() {
    this.validateInitialDates()
    window.addEventListener(
      'resize',
      _debounce(() => {
        this.setWindowWidth()
      }, 250),
    )
  }

  componentWillUnmount = () => {
    const { dates, syncFiltersAction } = this.props
    const { isForceFilterUpdate } = this.state
    if (Array.isArray(dates) && dates.length < 2) {
      this.changeStore(null, false)
    }

    if (isForceFilterUpdate) {
      syncFiltersAction(true)
    }

    window.removeEventListener('resize', this.setWindowWidth)
  }

  setWindowWidth = () => {
    this.setState({
      width: window.innerWidth,
    })
  }

  validateInitialDates = async () => {
    const { dates } = this.props;
    const [start] = dates;
    const today = new Date();

    if (!!start && start.getTime() < today.getTime()) {
      await this.changeStore([today, new Date(today.getTime() + 24 * 60 * 60 * 1000)], true)
      this.setState({ isForceFilterUpdate: true })
    }
  }

  handlePeriodChange = (range) => {
    const {
      onSubmitHandler,
      pageIndex,
      browser,
    } = this.props
    if (!oneDayBooking && range[1] && range[0].getTime() === range[1].getTime()) {
      range[1] = null
    }

    if (oneDayBooking) {
      range[1] = range[0]
    }

    this.changeStore(range, false)
    this.start = URLDateParam(range[0])
    this.end = range[1] ? URLDateParam(range[1]) : null

    if (range[1] > 0 && pageIndex === PAGE_HOTEL && isDesktop(browser)) {
      setTimeout(() => {
        onSubmitHandler(false)
      }, 100)
    }
  }

  handleMonthChange = (date) => {
    const today = new Date()
    const currentMonth = today.getMonth() + 1
    const currentYear = today.getFullYear()
    const dateMonth = date.getMonth() + 1
    const dateYear = date.getFullYear()
    const datepicker = this.datepickerRef.current
    const prevMonthBtn = datepicker.querySelector('.react-datepicker__navigation--previous')

    if (dateMonth <= currentMonth && dateYear <= currentYear) {
      prevMonthBtn.classList.add('disabled')
    } else {
      prevMonthBtn.classList.remove('disabled')
    }
  }

  handleActiveDateChange = () => {
    this.monthDays = []
    const { handleChangeMonth } = this.props
    handleChangeMonth({ id: generateId() })
  }

  handleDayClass = (date) => {
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0)
    const today = new Date().setHours(0, 0, 0, 0)
    const formattedDate = URLDateParam(date)

    if (date.getTime() < today) {
      return 'react-datepicker__day--disabled'
    }

    if (date.getDate() === lastDay.getDate()) {
      return 'last-day'
    }

    if (offersMockedData && !isEmpty(offersMockedData)) {
      const { from, to } = offersMockedData

      if (!!from && !!to && formattedDate >= from && formattedDate <= to) {
        return 'offer'
      }
    }

    return false
  }

  handleInputRange = () => {
    const startDateValue = this.inputStart.current.value
    const endDateValue = this.inputEnd.current.value
    let startDate = startDateValue ? new Date(startDateValue) : null
    let endDate = endDateValue ? new Date(endDateValue) : null


    if (startDate && startDate <= new Date()) {
      startDate = new Date()
    }

    if (endDate && endDate < startDate) {
      endDate = ''
    }

    this.changeStore([startDate, endDate], false)
  }

  changeStore = async (datesRange, apply) => {
    const { filterChangeAction, filterApplyAction, searchFilters } = this.props
    await filterChangeAction(FILTER_DATES, datesRange, searchFilters.additionalValues)
    await filterApplyAction(FILTER_DATES, apply, datesRange)
  }

  getEndDateValue = (dates) => {
    if (oneDayBooking && dates && dates[0]) return dates[0]

    if (!oneDayBooking && dates && dates[1]) return dates[1]

    return null
  }

  render() {
    const {
      dates,
      getSelectedFields,
      calendarText,
      calendarWithError,
    } = this.props
    const { width } = this.state

    return (
      <React.Fragment>
        <div className="filter-block-header__selected">
          {parse(getSelectedFields())}

          {isTouchDevice() && (
            <div className="hidden-range">
              <input
                className="input-range__start"
                type="date"
                onChange={() => {
                  this.handleInputRange()
                }}
                ref={this.inputStart}
                value={dates && dates[0] ? URLDateParam(dates[0]) : ''}
                min={URLDateParam(new Date())}
              />
              <span className="input-range__divider" />
              <input
                className="input-range__end"
                type="date"
                onChange={() => {
                  this.handleInputRange()
                }}
                ref={this.inputEnd}
                disabled={!(dates && dates[0])}
                value={dates && dates[1] ? URLDateParam(dates[1]) : ''}
                min={dates && dates[0] ? URLDateParam(dates[0]) : ''}
              />
            </div>
          )}
        </div>

        <CustomScrollBar>
          { calendarText && calendarWithError && (
            <span className="filter-block-error">
            {parse(calendarText)}
          </span>
          )}
          <div className="filter-dates" ref={this.datepickerRef}>
            <Field
              className="filter-dates__calendar"
              name="dates"
              component={DatePicker}
              props={{
                selected: dates && dates[0] ? dates[0] : null,
                onChange: this.handlePeriodChange,
                onMonthChange: this.handleMonthChange,
                startDate: dates && dates[0] ? dates[0] : null,
                endDate: this.getEndDateValue(dates),
                selectsRange: true,
                inline: true,
                monthsShown: width > 767 ? 2 : 12,
                formatWeekDay: nameOfDay => nameOfDay.substr(0, 3),
                disabledKeyboardNavigation: true,
                shouldCloseOnSelect: false,
                locale: location,
                dayClassName: this.handleDayClass,
              }}
            />
          </div>
        </CustomScrollBar>
      </React.Fragment>
    )
  }
}

Dates.defaultProps = {
  locale: 'en-GB',
  dates: [],
  calendarText: '',
  pageIndex: 0,
}

Dates.propTypes = {
  locale: PropTypes.string,
  dates: PropTypes.arrayOf(Object),
  filterChangeAction: PropTypes.func.isRequired,
  filterApplyAction: PropTypes.func.isRequired,
  handleChangeMonth: PropTypes.func.isRequired,
  getSelectedFields: PropTypes.func.isRequired,
  calendarText: PropTypes.string,
  calendarWithError: PropTypes.bool.isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
  browser: PropTypes.objectOf(Object).isRequired,
  pageIndex: PropTypes.number,
}

export default Dates
